/* Progressive enhancement: without JavaScript, topic links lead to visible sections. */
if (!customElements.get('client-services')) {
  customElements.define('client-services', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.tabs = [...this.querySelectorAll('[data-tab]')];
      this.panels = [...this.querySelectorAll('[data-panel]')];
      if (!this.tabs.length) return;
      const navigation = this.querySelector('.client-services-page__navigation');
      navigation.setAttribute('role', 'tablist');
      navigation.setAttribute('aria-label', 'Client services topics');
      this.querySelector('nav').setAttribute('role', 'presentation');
      this.tabs.forEach(tab => {
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', tab.hash.slice(1));
        tab.addEventListener('click', event => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          this.select(tab, true);
        }, { signal });
        tab.addEventListener('keydown', event => {
          const index = this.tabs.indexOf(tab);
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
          if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;
          if (next === undefined) return;
          event.preventDefault();
          this.select(this.tabs[next], true);
          this.tabs[next].focus({ preventScroll: true });
        }, { signal });
      });
      this.panels.forEach(panel => {
        panel.setAttribute('role', 'tabpanel');
        panel.tabIndex = 0;
      });
      this.syncLocation = () => {
        const tab = this.tabForHash(location.hash);
        if (tab || !location.hash) this.select(tab || this.tabs[0]);
        this.openLinkedReturnPolicy();
      };
      window.addEventListener('hashchange', this.syncLocation, { signal });
      window.addEventListener('popstate', this.syncLocation, { signal });
      const formResult = this.querySelector('.client-services-page__form-message');
      this.select((formResult && this.tabForHash(this.dataset.supportHash)) || this.tabForHash(location.hash) || this.tabs[0]);
      if (formResult) formResult.focus();
      else this.openLinkedReturnPolicy();
      const scroller = this.querySelector('.client-services-page__tabs');
      const nextButton = this.querySelector('.client-services-page__next');
      const updateOverflow = () => {
        nextButton.hidden = scroller.scrollWidth <= scroller.clientWidth + 1;
        const atEnd = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 2;
        nextButton.textContent = atEnd ? '←' : '→';
        nextButton.setAttribute('aria-label', atEnd ? 'Show earlier topics' : 'Show more topics');
      };
      nextButton.addEventListener('click', () => {
        const atEnd = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 2;
        scroller.scrollTo({ left: atEnd ? 0 : scroller.scrollLeft + scroller.clientWidth * 0.7, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      }, { signal });
      scroller.addEventListener('scroll', updateOverflow, { signal, passive: true });
      this.observer = new ResizeObserver(updateOverflow);
      this.observer.observe(scroller);
      updateOverflow();
      document.addEventListener('shopify:block:select', event => {
        const panel = this.panels.find(item => item.dataset.blockId === event.detail?.blockId);
        if (panel) this.select(this.tabs.find(tab => tab.hash === '#' + panel.id));
      }, { signal });
      this.querySelectorAll('a[href="#client-services-contact"], [data-contact-link]').forEach(link => {
        link.addEventListener('click', event => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          const enquiry = this.querySelector('[data-inquiry]');
          const category = enquiry?.value;
          const supportTab = this.tabForHash(this.dataset.supportHash);
          if (!supportTab) return;
          this.select(supportTab, true);
          if (enquiry) enquiry.value = category || supportTab.dataset.label;
          this.querySelector('.client-services-page__request')?.scrollIntoView({ block: 'start' });
          enquiry?.focus({ preventScroll: true });
        }, { signal });
      });
    }
    openLinkedReturnPolicy() {
      if (location.hash.replaceAll('_', '-') !== '#cs-topic-returns') return;
      const panel = this.querySelector('[data-block-id="topic_returns"]');
      const policy = panel?.querySelector('details');
      if (policy && !panel.hidden) policy.open = true;
    }
    tabForHash(hash) {
      const normalizedHash = hash.replaceAll('_', '-');
      const direct = this.tabs.find(tab => tab.hash.replaceAll('_', '-') === normalizedHash);
      if (direct) return direct;
      if (hash === '#client-services-contact') return this.tabs.find(tab => tab.hash === this.dataset.supportHash);
      const target = [...this.querySelectorAll('[id]')].find(item => '#' + item.id === hash);
      const panel = target?.closest('[data-panel]');
      return panel && this.tabs.find(tab => tab.hash === '#' + panel.id);
    }
    select(tab, updateHistory = false) {
      if (!tab) return;
      this.tabs.forEach(item => {
        const active = item === tab;
        item.setAttribute('aria-selected', String(active));
        item.tabIndex = active ? 0 : -1;
      });
      this.panels.forEach(panel => { panel.hidden = '#' + panel.id !== tab.hash; });
      if (updateHistory && location.hash !== tab.hash) history.pushState(null, '', tab.hash);
      const enquiry = this.querySelector('[data-inquiry]');
      if (tab.dataset.label && enquiry && tab.hash !== this.dataset.supportHash) {
        const available = [...enquiry.options].some(option => option.value === tab.dataset.label);
        enquiry.value = available ? tab.dataset.label : 'Other enquiry';
      }
      const scroller = this.querySelector('.client-services-page__tabs');
      if (scroller.contains(tab)) {
        const rect = tab.getBoundingClientRect();
        const parentRect = scroller.getBoundingClientRect();
        if (rect.left < parentRect.left) scroller.scrollLeft -= parentRect.left - rect.left;
        else if (rect.right > parentRect.right) scroller.scrollLeft += rect.right - parentRect.right;
      }
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
    }
  });
}
